import React, { useState } from 'react';
import { useNotifications } from '../../context/NotificationContext';
import { cleanupLocalStorage } from '../../utils/storageCleanup';
import '../Dashboard/Dashboard.css';

const SettingsPage: React.FC = () => {
  const { showSuccess, showError } = useNotifications();
  
  // Password change form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    loading: false,
    showForm: false
  });

  // Delete account state
  const [deleteAccount, setDeleteAccount] = useState({
    showConfirm: false,
    confirmText: '',
    loading: false
  });

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showError('Validation Error', 'New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      showError('Validation Error', 'New password must be at least 6 characters long');
      return;
    }

    setPasswordForm(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await fetch('/api/users/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      if (response.ok) {
        showSuccess('Success', 'Password changed successfully');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
          loading: false,
          showForm: false
        });
      } else {
        const data = await response.json();
        showError('Error', data.message || 'Failed to change password');
      }
    } catch (error) {
      showError('Error', 'Network error while changing password');
    } finally {
      setPasswordForm(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteAccount.confirmText !== 'DELETE') {
      showError('Validation Error', 'Please type "DELETE" to confirm account deletion');
      return;
    }

    setDeleteAccount(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await fetch('/api/users/delete-account', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        showSuccess('Success', 'Account deleted successfully');
        // Clear all auth + game data using the canonical storage keys.
        // ('token'/'user' were the wrong keys and removed nothing, leaving a
        // stale session behind after account deletion.)
        cleanupLocalStorage({ clearAuth: true, clearGameData: true, verbose: false });
        window.location.href = '/login';
      } else {
        const data = await response.json();
        showError('Error', data.message || 'Failed to delete account');
      }
    } catch (error) {
      showError('Error', 'Network error while deleting account');
    } finally {
      setDeleteAccount(prev => ({ ...prev, loading: false }));
    }
  };
  return (
    <div className="settings-view">
      <div className="view-header modern-minimal">
        <div className="header-content">
          <div className="header-icon">⚙️</div>
          <div className="header-text">
            <h1>Settings</h1>
            <p>Configure your game preferences and account settings.</p>
          </div>
        </div>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <h2>Game Preferences</h2>
          <div className="settings-card">
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-label">Sound Effects</span>
                <span className="setting-description">Play sound effects during gameplay</span>
              </div>
              <div className="setting-control">
                <input type="checkbox" defaultChecked />
              </div>
            </div>
            
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-label">Auto-Ready</span>
                <span className="setting-description">Automatically mark as ready in game lobbies</span>
              </div>
              <div className="setting-control">
                <input type="checkbox" />
              </div>
            </div>
            
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-label">Animation Speed</span>
                <span className="setting-description">Speed of card animations</span>
              </div>
              <div className="setting-control">
                <select defaultValue="normal">
                  <option value="slow">Slow</option>
                  <option value="normal">Normal</option>
                  <option value="fast">Fast</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2>Notifications</h2>
          <div className="settings-card">
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-label">Game Invites</span>
                <span className="setting-description">Receive notifications for game invitations</span>
              </div>
              <div className="setting-control">
                <input type="checkbox" defaultChecked />
              </div>
            </div>
            
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-label">Turn Reminders</span>
                <span className="setting-description">Get notified when it's your turn</span>
              </div>
              <div className="setting-control">
                <input type="checkbox" defaultChecked />
              </div>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2>Account</h2>
          <div className="settings-card">
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-label">Change Password</span>
                <span className="setting-description">Update your account password</span>
              </div>
              <div className="setting-control">
                <button 
                  className="btn-secondary"
                  onClick={() => setPasswordForm(prev => ({ ...prev, showForm: !prev.showForm }))}
                >
                  {passwordForm.showForm ? 'Cancel' : 'Change'}
                </button>
              </div>
            </div>
            
            {/* Password Change Form */}
            {passwordForm.showForm && (
              <div className="setting-item">
                <div style={{ width: '100%' }}>
                  <h3 style={{ color: 'white', marginBottom: '1rem' }}>Change Password</h3>
                  <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', color: '#ccc', marginBottom: '0.5rem' }}>Current Password</label>
                      <input
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                        required
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem', 
                          backgroundColor: '#333', 
                          color: 'white', 
                          border: '1px solid #555', 
                          borderRadius: '4px' 
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#ccc', marginBottom: '0.5rem' }}>New Password</label>
                      <input
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                        minLength={6}
                        required
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem', 
                          backgroundColor: '#333', 
                          color: 'white', 
                          border: '1px solid #555', 
                          borderRadius: '4px' 
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#ccc', marginBottom: '0.5rem' }}>Confirm New Password</label>
                      <input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        required
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem', 
                          backgroundColor: '#333', 
                          color: 'white', 
                          border: '1px solid #555', 
                          borderRadius: '4px' 
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button
                        type="submit"
                        disabled={passwordForm.loading}
                        className="btn-primary"
                        style={{ flex: '1' }}
                      >
                        {passwordForm.loading ? 'Changing...' : 'Change Password'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPasswordForm(prev => ({ ...prev, showForm: false, currentPassword: '', newPassword: '', confirmPassword: '' }))}
                        className="btn-secondary"
                        style={{ flex: '1' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-label">Clear Cache & Storage</span>
                <span className="setting-description">Clear cached game data and fix stale room issues (fixes 404 errors)</span>
              </div>
              <div className="setting-control">
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    cleanupLocalStorage({ clearGameData: true, verbose: true });
                    showSuccess('Cache Cleared', 'Game cache and stale data cleared successfully. Refresh the page if issues persist.');
                  }}
                >
                  Clear Cache
                </button>
              </div>
            </div>
            
            <div className="setting-item">
              <div className="setting-info">
                <span className="setting-label">Delete Account</span>
                <span className="setting-description">Permanently delete your account and data</span>
              </div>
              <div className="setting-control">
                <button 
                  className="btn-danger"
                  onClick={() => setDeleteAccount(prev => ({ ...prev, showConfirm: !prev.showConfirm }))}
                >
                  {deleteAccount.showConfirm ? 'Cancel' : 'Delete'}
                </button>
              </div>
            </div>
            
            {/* Delete Account Confirmation */}
            {deleteAccount.showConfirm && (
              <div className="setting-item" style={{ background: '#2a1f1f', border: '1px solid #ff4444' }}>
                <div style={{ width: '100%' }}>
                  <h3 style={{ color: '#ff4444', marginBottom: '1rem' }}>⚠️ Delete Account</h3>
                  <p style={{ color: '#ccc', marginBottom: '1rem' }}>
                    This action cannot be undone. This will permanently delete your account and all associated data including:
                  </p>
                  <ul style={{ color: '#ccc', marginBottom: '1rem', paddingLeft: '1.5rem' }}>
                    <li>Your profile and game statistics</li>
                    <li>Game history and achievements</li>
                    <li>All room memberships</li>
                    <li>Account preferences and settings</li>
                  </ul>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', color: '#ff4444', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Type "DELETE" to confirm:
                    </label>
                    <input
                      type="text"
                      value={deleteAccount.confirmText}
                      onChange={(e) => setDeleteAccount(prev => ({ ...prev, confirmText: e.target.value }))}
                      placeholder="Type DELETE here"
                      style={{ 
                        width: '100%', 
                        padding: '0.5rem', 
                        backgroundColor: '#333', 
                        color: 'white', 
                        border: '1px solid #ff4444', 
                        borderRadius: '4px' 
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleteAccount.loading || deleteAccount.confirmText !== 'DELETE'}
                      className="btn-danger"
                      style={{ 
                        flex: '1',
                        opacity: deleteAccount.confirmText !== 'DELETE' ? 0.5 : 1
                      }}
                    >
                      {deleteAccount.loading ? 'Deleting...' : 'DELETE ACCOUNT PERMANENTLY'}
                    </button>
                    <button
                      onClick={() => setDeleteAccount({ showConfirm: false, confirmText: '', loading: false })}
                      className="btn-secondary"
                      style={{ flex: '1' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
